import { cn } from "@/lib/utils";

type BadgeVariant = "neutral" | "primary" | "success" | "warning" | "error" | "info" | "highlight";
type BadgeStyle = "subtle" | "solid" | "outline";

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  appearance?: BadgeStyle;
}

const subtleMap: Record<BadgeVariant, string> = {
  neutral: "bg-ink-100 text-ink-700",
  primary: "bg-primary-50 text-primary-700",
  success: "bg-success-50 text-success-700",
  warning: "bg-warning-50 text-warning-700",
  error: "bg-error-50 text-error-700",
  info: "bg-info-50 text-info-600",
  highlight: "bg-highlight-50 text-highlight-700",
};

const solidMap: Record<BadgeVariant, string> = {
  neutral: "bg-ink-700 text-white",
  primary: "bg-primary-500 text-on-primary",
  success: "bg-success-500 text-white",
  warning: "bg-warning-500 text-white",
  error: "bg-error-500 text-white",
  info: "bg-info-500 text-white",
  highlight: "bg-highlight-500 text-white",
};

const outlineMap: Record<BadgeVariant, string> = {
  neutral: "border border-border text-ink-700 bg-surface",
  primary: "border border-primary-200 text-primary-700 bg-surface",
  success: "border border-success-100 text-success-700 bg-surface",
  warning: "border border-warning-100 text-warning-700 bg-surface",
  error: "border border-error-100 text-error-700 bg-surface",
  info: "border border-info-100 text-info-600 bg-surface",
  highlight: "border border-highlight-100 text-highlight-700 bg-surface",
};

export function Badge({
  variant = "neutral",
  appearance = "subtle",
  className,
  children,
  ...props
}: BadgeProps) {
  const styleClass =
    appearance === "solid" ? solidMap[variant] : appearance === "outline" ? outlineMap[variant] : subtleMap[variant];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium leading-none",
        styleClass,
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}
